import React from 'react';
import { Link } from 'react-router-dom';
import './ServiceTemplate.css';

function EssentialSkills() {
  return (
    <div className="service-page">
      <section className="service-hero" style={{backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(https://customer-assets.emergentagent.com/job_fitlaunch/artifacts/zz5fdv5i_20250723_133519.jpeg)`}}>
        <div className="container">
          <h1><span className="fit-inline"><span className="f-blue">F</span>-YOU</span> Essential Skills</h1>
          <p style={{color: '#FF6B35', fontSize: '1.1em', marginBottom: '10px', fontWeight: '500', fontStyle: 'italic'}}>Born from lived experience, built on psychology, driven by passion</p>
          <p className="subtitle">Master the critical conversations and skills that prevent F-IT becoming F-YOU</p>
        </div>
      </section>

      {/* White section with F-YOU logo repeating pattern */}
      <div style={{
        width: '100%', 
        padding: '60px 20px', 
        position: 'relative',
        background: `
          linear-gradient(rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.95)),
          url(/fyou-logo-edit.jpeg)
        `,
        backgroundSize: 'auto, 100px 100px',
        backgroundRepeat: 'no-repeat, repeat',
        backgroundPosition: '0 0, 0 0'
      }}>
        <div style={{maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1}}>
          <h2 style={{fontSize: '36px', color: '#2c3e50', marginBottom: '20px', fontFamily: 'Arial, sans-serif', fontWeight: 'bold'}}>The Critical Window of Opportunity</h2>
          <p>Here's the reality: <strong>You can speak with someone who has a high F-IT score, but it's impossible to communicate with someone who has a high F-YOU score.</strong></p>
          <p>When an employee is struggling with their wellbeing (high F-IT), they're still reachable. They still want to be heard. They're still hoping for support. This is your window - the moment when the right conversation, the right response, can make all the difference.</p>
          <p>But if that person feels dismissed, ignored, or unsupported - if their cry for help is met with indifference or criticism - their F-IT transforms into F-YOU. And once someone reaches that point of disengagement and resentment, they've checked out. No amount of wellbeing initiatives or engagement campaigns will reach them.</p>
          <p style={{backgroundColor: '#FFF5F0', padding: '20px', borderLeft: '5px solid #FF6B35', margin: '20px 0'}}>
            <strong>The Essential Skills training focuses on this critical window.</strong> It equips managers and supervisors with the practical skills to have effective F-IT conversations - catching people when they're still reachable and preventing the transformation to F-YOU.
          </p>
        </div>
      </div>

      {/* Grey section */}
      <div style={{backgroundColor: '#f9f9f9', width: '100%', padding: '80px 20px'}}>
        <div style={{maxWidth: '1200px', margin: '0 auto'}}>
          <h2 style={{fontSize: '36px', color: '#2c3e50', marginBottom: '20px', fontFamily: 'Arial, sans-serif', fontWeight: 'bold'}}>Why These Conversations Are So Critical</h2>
          <p>Most managers mean well. They care about their teams. But many lack the specific skills to respond effectively when someone shows signs of high F-IT. They:</p>
          <ul>
            <li>Dismiss concerns as "just having a bad day"</li>
            <li>Give well-meaning but unhelpful advice ("just stay positive")</li>
            <li>Focus on the task rather than the person</li>
            <li>Avoid the conversation entirely because they're uncomfortable</li>
            <li>Respond with criticism when performance drops due to wellbeing issues</li>
          </ul>
          <p>Every one of these responses pushes someone closer to F-YOU. <strong>The Essential Skills training gives you the tools to respond differently.</strong></p>
        </div>
      </div>

      {/* White section with image */}
      <div style={{width: '100%', padding: '80px 20px'}}>
        <div style={{maxWidth: '1200px', margin: '0 auto'}}>
          <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', alignItems: 'center'}}>
            <div>
              <h2 style={{fontSize: '36px', color: '#2c3e50', marginBottom: '20px', fontFamily: 'Arial, sans-serif', fontWeight: 'bold'}}>What You'll Learn</h2>
              <p>This is hands-on, practical training in the day-to-day skills that prevent F-IT becoming F-YOU and create a positive, supportive culture.</p>
            </div>
            <div style={{textAlign: 'center'}}>
              <img src="https://images.unsplash.com/photo-1475721027785-f74eccf877e2?w=600&q=80" alt="Speaker presenting to professional audience" style={{maxWidth: '100%', height: 'auto', borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.15)'}} />
            </div>
          </div>
        </div>
      </div>

      {/* Grey section - Skills 1 & 2 */}
      <div style={{backgroundColor: '#f2f2f2', width: '100%', padding: '80px 20px'}}>
        <div style={{maxWidth: '1200px', margin: '0 auto'}}>
          <h3 style={{fontSize: '28px', color: '#2c3e50', marginBottom: '15px', fontFamily: 'Arial, sans-serif', fontWeight: 'bold'}}>1. Active Listening</h3>
          <p>Not just hearing words, but truly understanding what someone is communicating - especially what they're not saying directly. Learn to:</p>
          <ul>
            <li>Recognise verbal and non-verbal signs of high F-IT</li>
            <li>Create psychological safety for honest conversations</li>
            <li>Listen without immediately problem-solving or dismissing</li>
            <li>Validate feelings without agreeing or disagreeing</li>
            <li>Use silence effectively</li>
          </ul>

          <h3 style={{fontSize: '28px', color: '#2c3e50', marginBottom: '15px', marginTop: '40px', fontFamily: 'Arial, sans-serif', fontWeight: 'bold'}}>2. The Power of Appropriate Praise</h3>
          <p>Research shows that appropriate praise is <strong>20 times more effective than criticism</strong> in changing behaviour. Yet most managers dramatically under-use it. Learn to:</p>
          <ul>
            <li>Recognise and reinforce positive behaviours immediately</li>
            <li>Give specific, meaningful praise (not generic "good job")</li>
            <li>Use praise to build resilience and prevent high F-IT</li>
            <li>Balance recognition with constructive feedback</li>
            <li>Create a culture where people feel valued before they're struggling</li>
          </ul>
        </div>
      </div>

      {/* White section - Skills 3 & 4 */}
      <div style={{width: '100%', padding: '80px 20px'}}>
        <div style={{maxWidth: '1200px', margin: '0 auto'}}>
          <h3 style={{fontSize: '28px', color: '#2c3e50', marginBottom: '15px', fontFamily: 'Arial, sans-serif', fontWeight: 'bold'}}>3. Constructive Feedback & Difficult Conversations</h3>
          <p>When performance drops due to high F-IT, how you address it determines whether that person gets support or develops F-YOU. Learn to:</p>
          <ul>
            <li>Give negative feedback without crushing morale</li>
            <li>Address performance issues while showing care for the person</li>
            <li>Have difficult conversations that strengthen relationships rather than damage them</li>
            <li>Distinguish between wellbeing-related performance issues and other causes</li>
            <li>Set boundaries compassionately</li>
          </ul>

          <h3 style={{fontSize: '28px', color: '#2c3e50', marginBottom: '15px', marginTop: '40px', fontFamily: 'Arial, sans-serif', fontWeight: 'bold'}}>4. Coaching & Supporting Through High F-IT</h3>
          <p>When someone is struggling, they don't need a manager - they need a coach. Learn to:</p>
          <ul>
            <li>Ask high-value questions that help people find their own solutions</li>
            <li>Support without taking over or solving everything for them</li>
            <li>Help people identify their own coping strategies and resources</li>
            <li>Know when to listen, when to advise, and when to escalate</li>
            <li>Build confidence and autonomy even during difficult periods</li>
          </ul>
        </div>
      </div>

      {/* Grey section - Skills 5, 6 & 7 */}
      <div style={{backgroundColor: '#ebebeb', width: '100%', padding: '80px 20px'}}>
        <div style={{maxWidth: '1200px', margin: '0 auto'}}>
          <h3 style={{fontSize: '28px', color: '#2c3e50', marginBottom: '15px', fontFamily: 'Arial, sans-serif', fontWeight: 'bold'}}>5. Assertion Skills</h3>
          <p>Being supportive doesn't mean being passive. Managers need to assert boundaries, expectations, and standards while maintaining respect. Learn to:</p>
          <ul>
            <li>Say no without damaging relationships</li>
            <li>Set clear expectations compassionately</li>
            <li>Assert your own needs and boundaries</li>
            <li>Navigate conflicts constructively</li>
            <li>Balance organisational needs with individual wellbeing</li>
          </ul>

          <h3 style={{fontSize: '28px', color: '#2c3e50', marginBottom: '15px', marginTop: '40px', fontFamily: 'Arial, sans-serif', fontWeight: 'bold'}}>6. Effective Delegation</h3>
          <p>Poor delegation creates high F-IT (overloading some people while underutilising others). Effective delegation distributes work fairly and builds capability. Learn to:</p>
          <ul>
            <li>Match tasks to people's strengths and development needs</li>
            <li>Delegate authority, not just tasks</li>
            <li>Support without micromanaging</li>
            <li>Build team capability while protecting individual wellbeing</li>
            <li>Recognise when workload is creating high F-IT scores</li>
          </ul>

          <h3 style={{fontSize: '28px', color: '#2c3e50', marginBottom: '15px', marginTop: '40px', fontFamily: 'Arial, sans-serif', fontWeight: 'bold'}}>7. Mental Health Conversations</h3>
          <p>High F-IT is often rooted in mental health challenges. Managers need confidence to:</p>
          <ul>
            <li>Recognise signs of declining mental health</li>
            <li>Start conversations about wellbeing without being intrusive</li>
            <li>Respond to disclosures appropriately</li>
            <li>Signpost to professional support when needed</li>
            <li>Create an environment where mental health conversations are normalised</li>
          </ul>
        </div>
      </div>

      {/* White section - Prevention Framework */}
      <div style={{width: '100%', padding: '80px 20px'}}>
        <div style={{maxWidth: '1200px', margin: '0 auto'}}>
          <h2 style={{fontSize: '36px', color: '#2c3e50', marginBottom: '20px', fontFamily: 'Arial, sans-serif', fontWeight: 'bold'}}>The F-IT to F-YOU Prevention Framework</h2>
          <p>Throughout the training, we emphasise the critical timeline:</p>
          
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginTop: '30px'}}>
            <div style={{backgroundColor: '#f9f9f9', padding: '25px', borderRadius: '8px', borderTop: '4px solid #4CAF50'}}>
              <h3 style={{fontSize: '20px', color: '#2c3e50', marginBottom: '10px'}}>Stage 1: Before F-IT</h3>
              <p><strong>Prevention:</strong> Use praise, recognition, good communication, and fair workload distribution to maintain wellbeing</p>
            </div>
            <div style={{backgroundColor: '#f9f9f9', padding: '25px', borderRadius: '8px', borderTop: '4px solid #FF9800'}}>
              <h3 style={{fontSize: '20px', color: '#2c3e50', marginBottom: '10px'}}>Stage 2: Early F-IT</h3>
              <p><strong>Intervention:</strong> Spot the signs early, have supportive conversations, adjust workload, offer resources</p>
            </div>
            <div style={{backgroundColor: '#f9f9f9', padding: '25px', borderRadius: '8px', borderTop: '4px solid #FF6B35'}}>
              <h3 style={{fontSize: '20px', color: '#2c3e50', marginBottom: '10px'}}>Stage 3: High F-IT</h3>
              <p><strong>Crisis Response:</strong> Active listening, immediate support, professional help if needed, compassionate adjustments</p>
            </div>
            <div style={{backgroundColor: '#f9f9f9', padding: '25px', borderRadius: '8px', borderTop: '4px solid #f44336'}}>
              <h3 style={{fontSize: '20px', color: '#2c3e50', marginBottom: '10px'}}>Stage 4: F-YOU</h3>
              <p><strong>Too Late:</strong> Communication breaks down. Person is disengaged. Focus shifts to organisational culture rather than individual conversations</p>
            </div>
          </div>
          
          <p style={{marginTop: '30px'}}><strong>The Essential Skills training prepares you for Stages 1-3</strong> - equipping you to prevent F-YOU from ever developing.</p>
        </div>
      </div>

      {/* Grey section - Training Format */}
      <div style={{backgroundColor: '#f2f2f2', width: '100%', padding: '80px 20px'}}>
        <div style={{maxWidth: '1200px', margin: '0 auto'}}>
          <h2 style={{fontSize: '36px', color: '#2c3e50', marginBottom: '20px', fontFamily: 'Arial, sans-serif', fontWeight: 'bold'}}>Training Format</h2>
          <ul>
            <li><strong>Duration:</strong> 1 day intensive or 2 half-day sessions</li>
            <li><strong>Group Size:</strong> 6-12 participants for optimal practice and feedback</li>
            <li><strong>Delivery:</strong> Highly interactive with role-play, scenario work, and practical exercises</li>
            <li><strong>Facilitators:</strong> Prof. Tim Marsh or Senior Consultant</li>
            <li><strong>Customisation:</strong> Can be tailored with scenarios specific to your industry and organisation</li>
          </ul>
        </div>
      </div>

      {/* White section - Perfect For with image */}
      <div style={{width: '100%', padding: '80px 20px'}}>
        <div style={{maxWidth: '1200px', margin: '0 auto'}}>
          <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', alignItems: 'center'}}>
            <div style={{textAlign: 'center'}}>
              <img src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=600&q=80" alt="Professional business team in meeting" style={{maxWidth: '100%', height: 'auto', borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.15)'}} />
            </div>
            <div>
              <h2 style={{fontSize: '36px', color: '#2c3e50', marginBottom: '20px', fontFamily: 'Arial, sans-serif', fontWeight: 'bold'}}>Perfect For</h2>
              <ul>
                <li>Front-line managers and supervisors who directly support teams</li>
                <li>Team leaders responsible for day-to-day wellbeing conversations</li>
                <li>Organisations wanting focused skill training without full 2-day F-YOU workshop</li>
                <li>Managers who are technically excellent but need confidence in people skills</li>
                <li>Anyone who finds wellbeing conversations uncomfortable or difficult</li>
                <li>Organisations building mental health champion networks</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Grey section - What Makes This Different */}
      <div style={{backgroundColor: '#e8e8e8', width: '100%', padding: '80px 20px'}}>
        <div style={{maxWidth: '1200px', margin: '0 auto'}}>
          <h2 style={{fontSize: '36px', color: '#2c3e50', marginBottom: '20px', fontFamily: 'Arial, sans-serif', fontWeight: 'bold'}}>What Makes This Different</h2>
          <ul>
            <li><strong>Focused on the Critical Window:</strong> Not generic communication training - specifically about catching high F-IT before it becomes F-YOU</li>
            <li><strong>Based on Real Experience:</strong> Skills rooted in proven methodology, not corporate theory</li>
            <li><strong>Immediately Applicable:</strong> Practice scenarios from your actual workplace</li>
            <li><strong>Honest & Direct:</strong> Uses the language of F-IT and F-YOU to make concepts memorable and real</li>
            <li><strong>Evidence-Based:</strong> Built on research (like the 20:1 praise-to-criticism ratio) and practical experience</li>
            <li><strong>Humanistic Approach:</strong> Skills that genuinely help people, not just tick compliance boxes</li>
          </ul>
        </div>
      </div>

      {/* Pricing section */}
      <div style={{width: '100%', padding: '80px 20px'}}>
        <div style={{maxWidth: '800px', margin: '0 auto', backgroundColor: '#fff', padding: '40px', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)'}}>
          <h3 style={{fontSize: '28px', color: '#2c3e50', marginBottom: '20px', fontFamily: 'Arial, sans-serif', fontWeight: 'bold'}}>Investment</h3>
          <p><strong>1-Day Essential Skills Training:</strong></p>
          <p><strong>Prof. Tim Marsh:</strong> £2,995 plus expenses and VAT</p>
          <p><strong>Senior Consultant:</strong> £1,995 plus expenses and VAT</p>
          <p><em>Groups of 6-12 participants</em></p>
          
          <p style={{marginTop: '20px'}}><strong>Alternative Format:</strong></p>
          <p>2 half-day sessions (can be scheduled across different weeks)</p>
          <p><em>Same pricing as 1-day intensive</em></p>
          
          <p style={{marginTop: '20px', fontSize: '14px', fontStyle: 'italic'}}>Can be combined with F-YOU Workshop for comprehensive culture transformation programme</p>
        </div>
      </div>

      <section className="cta-section">
        <div className="container">
          <h2>Ready to Master the Critical Conversations?</h2>
          <p>Equip your managers with the skills to prevent F-IT becoming&nbsp;F-YOU</p>
          <div style={{display: 'flex', gap: '30px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '30px'}}>
            <div style={{backgroundColor: 'rgba(255,255,255,0.1)', padding: '25px', borderRadius: '8px', textAlign: 'center', minWidth: '250px'}}>
              <h3 style={{color: '#FF6B35', marginBottom: '10px'}}>Book a Consultation</h3>
              <p style={{fontSize: '14px', marginBottom: '15px'}}>Discuss your organisation's training needs</p>
              <Link to="/contact" className="cta-btn cta-btn-primary">Get In Touch</Link>
            </div>
            <div style={{backgroundColor: 'rgba(255,255,255,0.1)', padding: '25px', borderRadius: '8px', textAlign: 'center', minWidth: '250px'}}>
              <h3 style={{color: '#FF6B35', marginBottom: '10px'}}>Explore Services</h3>
              <p style={{fontSize: '14px', marginBottom: '15px'}}>See our full range of workshops and training</p>
              <Link to="/how-it-works" className="cta-btn cta-btn-secondary">View All Services</Link>
            </div>
          </div>
          <div style={{marginTop: '30px', fontSize: '15px'}}>
            <p style={{margin: '5px 0'}}>01636 550190 option 5 <span style={{fontSize: '13px', opacity: '0.8'}}>(Mon-Fri 9am-5pm)</span></p>
            <p style={{margin: '5px 0'}}>hello@f-itscore.com</p>
          </div>
        </div>
      </section>
    </div>
  );
}

export default EssentialSkills;
